import { useState, useEffect } from "react";
import AxiosPastorVerification from "./Axios";

interface PastorRegistration {
  id: number;
  email: string;
  auth_id: string | null;
  full_name: string | null;
  church_name: string | null;
  denomination: string | null;
  country: string | null;
  phone: string | null;
  certificate_url: string | null;
  certificate_name: string | null;
  verification_status: string;
  admin_notes: string | null;
  created_at: string;
}

const PastorVerification = () => {
  const [pastors, setPastors] = useState<PastorRegistration[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [filter, setFilter] = useState<string>("all");

  const loadPastors = async () => {
    setLoading(true);
    try {
      const result = await AxiosPastorVerification.getAllPastors();
      if (result.data) {
        setPastors(result.data);
      } else if (result.error) {
        alert(`Error loading pastor registrations: ${result.error}`);
      }
    } catch (error) {
      console.error("Error loading pastors:", error);
      alert("An error occurred while loading pastor registrations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPastors();
  }, []);

  const handleApprove = async (id: number) => {
    setActionLoading(id);
    try {
      const result = await AxiosPastorVerification.approvePastor(id);
      if (result.success) {
        setPastors((prev) =>
          prev.map((p) =>
            p.id === id ? { ...p, verification_status: "approved" } : p
          )
        );
      } else {
        alert(`Error approving: ${result.error}`);
      }
    } catch (error) {
      console.error("Error approving pastor:", error);
      alert("An error occurred while approving");
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (id: number) => {
    const confirmed = window.confirm(
      "Are you sure you want to reject this pastor registration?"
    );
    if (!confirmed) return;

    setActionLoading(id);
    try {
      const result = await AxiosPastorVerification.rejectPastor(id);
      if (result.success) {
        setPastors((prev) =>
          prev.map((p) =>
            p.id === id ? { ...p, verification_status: "rejected" } : p
          )
        );
      } else {
        alert(`Error rejecting: ${result.error}`);
      }
    } catch (error) {
      console.error("Error rejecting pastor:", error);
      alert("An error occurred while rejecting");
    } finally {
      setActionLoading(null);
    }
  };

  const filteredPastors = pastors.filter((p) => {
    if (filter === "all") return true;
    return p.verification_status === filter;
  });

  const statusBadge = (status: string) => {
    if (status === "approved") {
      return (
        <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-800">
          Approved
        </span>
      );
    }
    if (status === "rejected") {
      return (
        <span className="rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-800">
          Rejected
        </span>
      );
    }
    return (
      <span className="rounded-full bg-yellow-100 px-3 py-1 text-xs font-semibold text-yellow-800">
        Pending
      </span>
    );
  };

  return (
    <div className="m-5 rounded-lg p-4 shadow-md">
      <h2 className="mb-6 text-2xl font-bold text-gray-200">
        Pastor Verification
      </h2>

      {/* Filter buttons */}
      <div className="mb-6 flex gap-2">
        <button
          onClick={() => setFilter("all")}
          className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
            filter === "all"
              ? "bg-orange-500 text-white"
              : "bg-gray-700 text-gray-200 hover:bg-gray-600"
          }`}
        >
          All ({pastors.length})
        </button>
        <button
          onClick={() => setFilter("pending")}
          className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
            filter === "pending"
              ? "bg-orange-500 text-white"
              : "bg-gray-700 text-gray-200 hover:bg-gray-600"
          }`}
        >
          Pending ({pastors.filter((p) => p.verification_status === "pending").length})
        </button>
        <button
          onClick={() => setFilter("approved")}
          className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
            filter === "approved"
              ? "bg-orange-500 text-white"
              : "bg-gray-700 text-gray-200 hover:bg-gray-600"
          }`}
        >
          Approved ({pastors.filter((p) => p.verification_status === "approved").length})
        </button>
        <button
          onClick={() => setFilter("rejected")}
          className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
            filter === "rejected"
              ? "bg-orange-500 text-white"
              : "bg-gray-700 text-gray-200 hover:bg-gray-600"
          }`}
        >
          Rejected ({pastors.filter((p) => p.verification_status === "rejected").length})
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <p className="text-gray-500">Loading pastor registrations...</p>
        </div>
      ) : filteredPastors.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <p className="text-gray-500">
            No pastor registrations {filter !== "all" ? `with status "${filter}"` : ""}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-700">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-800 text-gray-200">
              <tr>
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Email</th>
                <th className="px-4 py-3 font-semibold">Church</th>
                <th className="px-4 py-3 font-semibold">Denomination</th>
                <th className="px-4 py-3 font-semibold">Country</th>
                <th className="px-4 py-3 font-semibold">Phone</th>
                <th className="px-4 py-3 font-semibold">Certificate</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredPastors.map((pastor) => (
                <tr
                  key={pastor.id}
                  className="bg-gray-700/50 hover:bg-gray-700/70 transition-colors"
                >
                  <td className="px-4 py-3 text-gray-200">
                    {pastor.full_name || "—"}
                  </td>
                  <td className="px-4 py-3 text-gray-300">{pastor.email}</td>
                  <td className="px-4 py-3 text-gray-300">
                    {pastor.church_name || "—"}
                  </td>
                  <td className="px-4 py-3 text-gray-300">
                    {pastor.denomination || "—"}
                  </td>
                  <td className="px-4 py-3 text-gray-300">
                    {pastor.country || "—"}
                  </td>
                  <td className="px-4 py-3 text-gray-300">
                    {pastor.phone || "—"}
                  </td>
                  <td className="px-4 py-3">
                    {pastor.certificate_url ? (
                      <a
                        href={pastor.certificate_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-orange-400 hover:text-orange-300 hover:underline"
                      >
                        {pastor.certificate_name || "View Certificate"}
                      </a>
                    ) : (
                      <span className="text-gray-500">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {statusBadge(pastor.verification_status)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      {pastor.verification_status !== "approved" && (
                        <button
                          onClick={() => handleApprove(pastor.id)}
                          disabled={actionLoading === pastor.id}
                          className="rounded-lg bg-green-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {actionLoading === pastor.id ? "..." : "Approve"}
                        </button>
                      )}
                      {pastor.verification_status !== "rejected" && (
                        <button
                          onClick={() => handleReject(pastor.id)}
                          disabled={actionLoading === pastor.id}
                          className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {actionLoading === pastor.id ? "..." : "Reject"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PastorVerification;
